import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Star, MessageCircle } from "lucide-react";
import { track } from "@/lib/analytics";

interface ComparisonFeature {
  feature: string;
  loxone: boolean | string;
  competitors: boolean | string;
  description?: string;
}

const comparisonData: ComparisonFeature[] = [
  {
    feature: "Centrale intelligentie",
    loxone: true,
    competitors: false,
    description: "Alles werkt samen vanuit één systeem"
  },
  {
    feature: "Cloud-afhankelijkheid",
    loxone: false,
    competitors: true,
    description: "Loxone werkt volledig lokaal"
  },
  {
    feature: "Aantal apps nodig",
    loxone: "1 app",
    competitors: "5-15 apps",
    description: "Eén app voor alle functies"
  },
  {
    feature: "Automatische leeralgoritmes",
    loxone: true,
    competitors: false,
    description: "Leert uw gewoontes automatisch aan"
  },
  {
    feature: "Industriële betrouwbaarheid",
    loxone: true,
    competitors: false,
    description: "99.9% uptime gegarandeerd"
  },
  {
    feature: "Energiebesparing",
    loxone: "30-40%",
    competitors: "5-15%",
    description: "Bewezen hogere besparingen"
  },
  {
    feature: "Compatibility merken",
    loxone: "300+ merken",
    competitors: "Beperkt",
    description: "Werkt met bijna alle merken"
  },
  {
    feature: "Privacy & beveiliging",
    loxone: "100% lokaal",
    competitors: "Cloud-risico's",
    description: "Geen data naar externe servers"
  }
];

const competitorLogos = [
  { name: "Philips Hue", logo: "🔵" },
  { name: "Google Home", logo: "🔴" },
  { name: "Apple HomeKit", logo: "🍎" },
  { name: "Samsung SmartThings", logo: "⚫" },
  { name: "KNX Systemen", logo: "🏢" }
];

export const LoxoneComparison = () => {
  return (
    <section className="py-20 bg-gradient-to-br from-background to-muted/30">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">
              Objectieve Vergelijking
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Loxone vs. <span className="text-muted-foreground">Andere Smart Home Systemen</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Waarom kiezen steeds meer eigenaren in Gelderland voor Loxone? 
              Ontdek het verschil tussen een <strong>echte smart home</strong> en losse slimme apparaten.
            </p>
          </div>

          {/* Competitor Landscape */}
          <div className="mb-12">
            <h3 className="text-xl font-semibold text-center mb-6">
              Populaire alternatieven in Nederland:
            </h3>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              {competitorLogos.map((competitor, index) => (
                <div key={index} className="flex items-center gap-2 bg-muted/50 px-4 py-2 rounded-full">
                  <span className="text-lg">{competitor.logo}</span>
                  <span className="text-sm font-medium">{competitor.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Comparison Table */}
          <Card className="overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10">
              <CardTitle className="text-center">
                Waarom Loxone de slimste keuze is voor uw smart home
              </CardTitle>
              <CardDescription className="text-center">
                Gebaseerd op 10+ jaar ervaring en 1000+ installaties in Nederland
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-4 font-semibold">Functionaliteit</th>
                      <th className="text-center p-4 font-semibold text-primary">
                        <div className="flex items-center justify-center gap-2">
                          <Star className="h-4 w-4" />
                          Loxone
                        </div>
                      </th>
                      <th className="text-center p-4 font-semibold text-muted-foreground">
                        Traditionele Systemen
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonData.map((item, index) => (
                      <tr key={index} className="border-b hover:bg-muted/20 transition-colors">
                        <td className="p-4">
                          <div>
                            <div className="font-medium">{item.feature}</div>
                            {item.description && (
                              <div className="text-sm text-muted-foreground mt-1">
                                {item.description}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="p-4 text-center">
                          {typeof item.loxone === 'boolean' ? (
                            item.loxone ? (
                              <CheckCircle className="h-5 w-5 text-primary mx-auto" />
                            ) : (
                              <XCircle className="h-5 w-5 text-muted-foreground mx-auto" />
                            )
                          ) : (
                            <Badge variant="default" className="bg-primary">
                              {item.loxone}
                            </Badge>
                          )}
                        </td>
                        <td className="p-4 text-center">
                          {typeof item.competitors === 'boolean' ? (
                            item.competitors ? (
                              <CheckCircle className="h-5 w-5 text-muted-foreground mx-auto" />
                            ) : (
                              <XCircle className="h-5 w-5 text-destructive mx-auto" />
                            )
                          ) : (
                            <Badge variant="outline" className="text-muted-foreground">
                              {item.competitors}
                            </Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Key Differentiators */}
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            <Card className="text-center p-6 border-primary/20">
              <div className="bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Totaaloplossing</h3>
              <p className="text-sm text-muted-foreground">
                Geen app-chaos meer. Alles werkt samen vanuit één intelligent systeem.
              </p>
            </Card>

            <Card className="text-center p-6 border-primary/20">
              <div className="bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Bewezen Resultaten</h3>
              <p className="text-sm text-muted-foreground">
                30-40% energiebesparing en 50.000 automatische handelingen per jaar.
              </p>
            </Card>

            <Card className="text-center p-6 border-primary/20">
              <div className="bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Lokale Expertise</h3>
              <p className="text-sm text-muted-foreground">
                SOL-VI: uw gecertificeerde Loxone partner in Gelderland sinds 2018.
              </p>
            </Card>
          </div>

          <div className="text-center mt-12">
            <p className="text-lg font-medium mb-6">
              Klaar om het verschil zelf te ervaren?
            </p>
            <Button 
              size="lg"
              onClick={() => {
                track("loxone_comparison_cta");
                (window as any).Calendly?.initPopupWidget?.({
                  url: 'https://calendly.com/sol-vi/new-meeting?primary_color=ff7800',
                  pageSettings: {
                    backgroundColor: 'ffffff',
                    hideEventTypeDetails: false,
                    hideLandingPageDetails: false,
                    primaryColor: 'ff7800',
                    textColor: '4d5055'
                  }
                });
              }}
            >
              <MessageCircle className="mr-2 h-4 w-4" />
              Plan Persoonlijke Demonstratie
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LoxoneComparison;